import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { SettingsSection, InputRow } from './SettingsUI';

// Smart-lock Bluetooth adapter selection. Mirrors config.bluetooth.hciDeviceId
// on the hub: blank => default adapter (hci0); a number => a specific adapter
// (e.g. a USB BT dongle at hci1). noble binds the adapter at hub startup, so a
// change requires a hub restart to take effect.
const BluetoothSettings = ({ config }) => {
  const { updateConfigSection } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const current = config?.bluetooth?.hciDeviceId;
  const [deviceId, setDeviceId] = useState(
    current === undefined || current === null ? '' : String(current)
  );

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const trimmed = deviceId.trim();
      let value = null;
      if (trimmed !== '') {
        const parsed = parseInt(trimmed, 10);
        if (Number.isNaN(parsed) || parsed < 0 || String(parsed) !== trimmed) {
          setStatus({
            type: 'error',
            message: 'Enter a whole adapter number (e.g. 0 or 1), or leave blank for the default.',
          });
          setSaving(false);
          return;
        }
        value = parsed;
      }

      await updateConfigSection('bluetooth', { hciDeviceId: value });

      setStatus({
        type: 'success',
        message:
          value === null
            ? 'Saved — using the built-in adapter (hci0). Restart the hub to apply.'
            : `Saved — smart locks will use adapter hci${value}. Restart the hub to apply.`,
      });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {status && (
        <Alert variant={status.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{status.message}</AlertDescription>
        </Alert>
      )}

      <SettingsSection title="Smart Lock Adapter">
        <InputRow
          label="Bluetooth adapter"
          type="number"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          placeholder="Default (hci0)"
          description="Which Bluetooth adapter smart locks use. Leave blank for the hub's built-in adapter (hci0). Enter a USB dongle's index (e.g. 1 — run `hciconfig` on the hub to find it) to run locks off a dedicated adapter. Takes effect after a hub restart."
        />
        <div className="px-4 py-3">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </SettingsSection>
    </div>
  );
};

export default BluetoothSettings;
